import { LoadingButton } from '@mui/lab';
import { ListItemSecondaryAction, ListItemText, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import React, { useMemo } from 'react';
import { useStyling } from '../common/Theme';
import DelimitedLabels from '../common/components/DelimitedLabels';
import I18n, { useI18n } from '../common/i18n/I18n';
import { ContactWithKey } from './helpers/peopleUtils';

type ListActionButtonsProps = {
    isAdded: boolean;
    person: ContactWithKey;
    onMarkClick: (p: ContactWithKey) => void;
    onUnmarkClick: (p: ContactWithKey) => void;
    isLoading: boolean;
    isDisabled: boolean;
};

export const ListActionButtons = ({
    isAdded,
    person,
    onMarkClick,
    onUnmarkClick,
    isLoading,
    isDisabled
}: ListActionButtonsProps) => {
    const { theme } = useStyling();

    return (
        <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center' }}>
            {isAdded && (
                <Typography sx={{ color: theme.palette.text.disabled, mr: 2 }}>
                    <I18n token="form.added" />
                </Typography>
            )}
            <LoadingButton
                variant="text"
                loading={isLoading}
                disabled={isDisabled}
                onClick={(e) => {
                    e.stopPropagation();
                    isAdded ? onUnmarkClick(person) : onMarkClick(person);
                }}
            >
                {isAdded ? <I18n token="form.remove" /> : <I18n token="form.add" />}
            </LoadingButton>
        </ListItemSecondaryAction>
    );
};

type KeyPeopleTileProps = {
    item: ContactWithKey;
    onMarkClick: (person: ContactWithKey) => void;
    onUnmarkClick: (person: ContactWithKey) => void;
    isLoading: { isSaving: boolean; person?: ContactWithKey };
    isDisabled?: boolean;
    showNoRole?: boolean;
};

export default ({ item: person, onMarkClick, onUnmarkClick, isLoading, isDisabled = false, showNoRole = true }: KeyPeopleTileProps) => {
    const noRole = useI18n('production.header.keyPeople.no.role.assigned');

    const delimitedLabels = useMemo(() => {
        const { productionRoles, roles } = person ?? {};

        const label =
            productionRoles?.length
                ? productionRoles.join(', ')
                : roles?.length
                    ? roles.join(', ')
                    : null;

        if (label) return [label];
        if (!showNoRole) return null;

        return [noRole];
    }, [person?.productionRoles, person?.roles, noRole, showNoRole]);

    const isSamePerson = isEqual({ ...isLoading.person, isKey: undefined }, { ...person, isKey: undefined });
    const isPersonLoading = isLoading.isSaving && isSamePerson;
    const isPersonDisabled = isDisabled || (isLoading.isSaving && !isSamePerson);

    if (!person) return null;

    return (
        <React.Fragment key={person.personReference?.id}>
            <ListItemText
                primary={<Typography noWrap>{`${person.name.firstName} ${person.name.lastName}`}</Typography>}
                secondary={delimitedLabels ? (<DelimitedLabels>{delimitedLabels}</DelimitedLabels>) : null}
            />
            <ListActionButtons
                isAdded={person?.isKey}
                person={person}
                onMarkClick={onMarkClick}
                onUnmarkClick={onUnmarkClick}
                isLoading={isPersonLoading}
                isDisabled={isPersonDisabled}
            />
        </React.Fragment>
    );
};
